import { computePosition, flip, shift } from '@floating-ui/dom';
import { posToDOMRect, ReactRenderer } from '@tiptap/react';
import { MentionList } from './mention-list.js';

import type { Editor } from '@tiptap/core';
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion';
import type { TMember } from '@shared/types/member';
import type { TApiResponse } from '@shared/types';
import { QUERY_KEYS } from '@src/shared/constants/query-keys.js';
import { queryClient } from '@src/core/api/query-client.js';
import { request } from '@core/api/request';

interface SuggestionPropsWithRef extends SuggestionProps {
  ref?: HTMLElement | unknown;
}

const updatePosition = (editor: Editor, element: HTMLElement): void => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      ),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export const MentionEditorExtension = () => ({
  items: async ({ query }: { query: string }): Promise<TMember[]> => {
    const params = { name: query.trim() };
    const res = await queryClient.fetchQuery<TApiResponse<TMember>>({
      queryKey: [QUERY_KEYS.getMembers, params],
      queryFn: async () =>
        await request<TApiResponse<TMember>>({
          url: 'members/',
          params,
        }),
    });
    return res.results || [];
  },

  render: () => {
    let component: ReactRenderer | null = null;

    return {
      onStart: (props: SuggestionPropsWithRef) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });
        props.ref = component.ref;

        component.element.style.position = 'absolute';
        component.element.style.zIndex = '99999';
        const editorElement = props.editor.options.element as HTMLElement;
        editorElement.appendChild(component.element);

        if (props.clientRect) {
          updatePosition(props.editor, component.element);
        }
      },

      onUpdate(props: SuggestionProps) {
        if (!component) return;
        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        updatePosition(props.editor, component.element);
      },

      onKeyDown(props: SuggestionKeyDownProps): boolean {
        if (!component) return false;
        if (props.event.key === 'Escape') {
          component.destroy();
          return true;
        }

        const ref = component.ref as {
          onKeyDown?: (props: SuggestionKeyDownProps) => boolean;
        };

        return ref.onKeyDown?.(props) ?? false;
      },

      onExit() {
        if (!component) return;
        component.element.remove();
        component.destroy();
      },
    };
  },
});
